import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { FileText, Plus, Edit, Copy, Trash2, Loader2, Search, Filter, Send, Eye, DollarSign, Calendar, Building2 } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { projectId } from '../utils/supabase/info';
import { EditProposalDialog } from './EditProposalDialog';
import { CreateProposalDialog } from './CreateProposalDialog';
import { storedFixedBidQuote } from '../utils/fixedBidMarkup';
import { markupNote } from '../utils/fixedBidMarkupText';

interface Proposal {
  id: string;
  name: string;
  description: string;
  companyId: string;
  companyName: string;
  status: 'draft' | 'pending_approval' | 'approved' | 'rejected';
  pricing?: {
    type: string;
    amount: number;
    hourlyRate?: number;
    estimatedHours?: number;
    /** A fixed bid's markup: `amount` is the bid total. */
    basePrice?: number;
    markupPercent?: number;
    markupAmount?: number;
  };
  timeline?: {
    startDate: string;
    endDate: string;
  };
  deliverables?: any[];
  createdBy: string;
  createdByName: string;
  createdAt: string;
  updatedAt?: string;
  version?: number;
}

interface ProposalsListProps {
  accessToken: string;
  currentUser: any;
  onViewProposal?: (proposalId: string) => void;
}

export function ProposalsList({ accessToken, currentUser, onViewProposal }: ProposalsListProps) {
  const [proposals, setProposals] = useState<Proposal[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [ownerFilter, setOwnerFilter] = useState<'my' | 'all'>('all');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedProposal, setSelectedProposal] = useState<Proposal | null>(null);

  useEffect(() => {
    loadProposals();
  }, []);

  const loadProposals = async () => {
    setLoading(true);
    try {
      // Get all engagements/SOWs
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/consultant/engagements`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        // Filter to only show proposals (draft, pending_approval, or rejected status)
        const proposalStatuses = ['draft', 'pending_approval', 'rejected'];
        const allProposals = (data.engagements || []).filter((eng: any) => 
          proposalStatuses.includes(eng.status)
        );
        setProposals(allProposals);
      } else {
        console.error('Failed to load proposals');
        toast.error('Failed to load proposals');
      }
    } catch (error) {
      console.error('Error loading proposals:', error);
      toast.error('Failed to load proposals');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (proposal: Proposal) => {
    setSelectedProposal(proposal);
    setEditDialogOpen(true);
  };

  const handleDelete = async (proposal: Proposal) => {
    if (!confirm(`Are you sure you want to delete "${proposal.name}"?`)) {
      return;
    }

    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/consultant/sows/${proposal.id}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        toast.success('Proposal deleted successfully');
        loadProposals();
      } else {
        toast.error('Failed to delete proposal');
      }
    } catch (error) {
      console.error('Error deleting proposal:', error);
      toast.error('Failed to delete proposal');
    }
  };

  const handleSendForApproval = async (proposal: Proposal) => {
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/consultant/sows/${proposal.id}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            status: 'pending_approval',
          }),
        }
      );

      if (response.ok) {
        toast.success('Proposal sent for approval');
        loadProposals();
      } else {
        toast.error('Failed to send proposal');
      }
    } catch (error) {
      console.error('Error sending proposal:', error);
      toast.error('Failed to send proposal');
    }
  };

  // Filter logic
  const filteredProposals = proposals.filter(proposal => {
    // Search filter
    const nameMatch = (proposal.name || '').toLowerCase().includes(searchTerm.toLowerCase());
    const companyMatch = (proposal.companyName || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesSearch = nameMatch || companyMatch;
    
    // Status filter
    const matchesStatus = statusFilter === 'all' || proposal.status === statusFilter;
    
    // Owner filter (BD Rep silo)
    const matchesOwner = ownerFilter === 'all' || proposal.createdBy === currentUser.id;
    
    return matchesSearch && matchesStatus && matchesOwner;
  });

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'draft':
        return 'secondary';
      case 'pending_approval':
        return 'default';
      case 'approved':
        return 'default';
      case 'rejected':
        return 'destructive';
      default:
        return 'secondary';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'draft':
        return 'Draft';
      case 'pending_approval':
        return 'Pending Approval';
      case 'approved':
        return 'Approved';
      case 'rejected':
        return 'Rejected';
      default:
        return status;
    }
  };

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <FileText className="size-5" />
                Proposals
              </CardTitle>
              <CardDescription>
                Draft and pending proposals for review
              </CardDescription>
            </div>
            <Button onClick={() => setCreateDialogOpen(true)}>
              <Plus className="size-4 mr-2" />
              Create Proposal
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
              <Input
                placeholder="Search proposals..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="pending_approval">Pending Approval</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
            {currentUser.role === 'bdRep' && (
              <Select value={ownerFilter} onValueChange={(val: any) => setOwnerFilter(val)}>
                <SelectTrigger className="w-full sm:w-[180px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="my">My Proposals</SelectItem>
                  <SelectItem value="all">All Proposals</SelectItem>
                </SelectContent>
              </Select>
            )}
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="size-8 animate-spin text-gray-400" />
            </div>
          ) : filteredProposals.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="size-12 text-gray-300 mx-auto mb-4" />
              <p className="text-muted-foreground mb-4">
                {searchTerm || statusFilter !== 'all' 
                  ? 'No proposals found matching your filters'
                  : 'No proposals yet'
                }
              </p>
              <Button onClick={() => setCreateDialogOpen(true)}>
                <Plus className="size-4 mr-2" />
                Create Your First Proposal
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredProposals.map(proposal => (
                <Card key={proposal.id}>
                  <CardContent className="py-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-medium truncate">{proposal.name}</h3>
                          <Badge variant={getStatusBadgeVariant(proposal.status)}>
                            {getStatusLabel(proposal.status)}
                          </Badge>
                        </div>
                        
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm text-muted-foreground">
                          <div className="flex items-center gap-2">
                            <Building2 className="size-4 shrink-0" />
                            <span className="truncate">{proposal.companyName || 'Unknown Company'}</span>
                          </div>
                          {proposal.pricing && (
                            <div className="flex items-center gap-2">
                              <DollarSign className="size-4 shrink-0" />
                              <span>${proposal.pricing.amount?.toLocaleString() || '0'}{markupNote(storedFixedBidQuote(proposal.pricing))}</span>
                            </div>
                          )}
                          {proposal.timeline?.endDate && (
                            <div className="flex items-center gap-2">
                              <Calendar className="size-4 shrink-0" />
                              <span>{new Date(proposal.timeline.endDate).toLocaleDateString()}</span>
                            </div>
                          )}
                        </div>
                        
                        {proposal.description && (
                          <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                            {proposal.description}
                          </p>
                        )}
                      </div>

                      <div className="flex gap-2 shrink-0">
                        {proposal.status === 'draft' && (
                          <Button
                            size="sm"
                            variant="default"
                            onClick={() => handleSendForApproval(proposal)}
                          >
                            <Send className="size-4 mr-2" />
                            Send
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEdit(proposal)}
                        >
                          <Edit className="size-4" />
                        </Button>
                        {proposal.status === 'draft' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDelete(proposal)}
                          >
                            <Trash2 className="size-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Proposal Dialog */}
      {selectedProposal && (
        <EditProposalDialog
          open={editDialogOpen}
          onClose={() => {
            setEditDialogOpen(false);
            setSelectedProposal(null);
          }}
          onSuccess={() => {
            loadProposals();
            setEditDialogOpen(false);
            setSelectedProposal(null);
          }}
          proposalId={selectedProposal.id}
          accessToken={accessToken}
        />
      )}

      {/* Create Proposal Dialog */}
      <CreateProposalDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSuccess={() => {
          loadProposals();
          setCreateDialogOpen(false);
        }}
        accessToken={accessToken}
        currentUser={currentUser}
      />
    </>
  );
}
